import { useEffect, useRef } from 'react';
import type { ChangeEventHandler, ComponentType, ReactNode } from 'react';
import { components } from 'react-select';
import { getCurrentYearLabel } from '../helper';

interface CurrentYearOptionProps {
  label?: string;
  hasValue?: boolean;
  innerProps?: { id?: string };
  selectProps?: { inputValue?: string };
  children?: ReactNode;
}

interface CurrentYearItemRendererProps {
  checked: boolean;
  option: { label: string };
  onClick: ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
}

const ReactSelectOption = components.Option as ComponentType<CurrentYearOptionProps>;

/**
 * How far `list` must scroll for `row` to sit in its vertical centre.
 * @param row - The row to bring into view.
 * @param list - The scrolling list that contains it.
 * @returns The pixel offset to add to the list's `scrollTop`.
 */
function centringOffset(row: HTMLElement, list: HTMLElement) {
  const rowBox = row.getBoundingClientRect();
  const listBox = list.getBoundingClientRect();
  return rowBox.top - listBox.top - (list.clientHeight - rowBox.height) / 2;
}

/**
 * react-select option row that centres the current year in the open list while nothing is selected
 * and nothing is typed. Pass it as `components={{ Option: CurrentYearOption }}`.
 * @param props - The option props react-select hands every row.
 * @returns The default react-select option row.
 */
export function CurrentYearOption(props: CurrentYearOptionProps) {
  const { label, hasValue, innerProps, selectProps } = props;
  const optionId = innerProps?.id;
  const isScrollTarget = label === getCurrentYearLabel() && !hasValue && !selectProps?.inputValue;
  useEffect(() => {
    const optionElement = isScrollTarget && optionId ? document.getElementById(optionId) : null;
    const menuList = optionElement?.parentElement;
    if (optionElement && menuList) menuList.scrollTop += centringOffset(optionElement, menuList);
  }, [isScrollTarget, optionId]);
  return <ReactSelectOption {...props} />;
}

/**
 * react-multi-select-component row, identical to the library default, that centres the current year
 * in the open list while nothing is ticked and nothing is typed. Pass it as `ItemRenderer`.
 * @param props - The row props react-multi-select-component hands every item.
 * @returns A checkbox row.
 */
export function CurrentYearItemRenderer({ checked, option, onClick, disabled = false }: CurrentYearItemRendererProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const isCurrentYear = option.label === getCurrentYearLabel();
  useEffect(() => {
    const row = rowRef.current?.closest('.select-item');
    const list = row?.closest('.options');
    if (!isCurrentYear || !(row instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    const searchInput = list.parentElement?.querySelector('.search input');
    const hasSearch = searchInput instanceof HTMLInputElement && searchInput.value !== '';
    if (hasSearch || list.querySelector('.select-item.selected')) return;
    list.scrollTop += centringOffset(row, list);
  }, [isCurrentYear]);
  return (
    <div ref={rowRef} className={`item-renderer ${disabled ? 'disabled' : ''}`}>
      <input type="checkbox" onChange={onClick} checked={checked} tabIndex={-1} disabled={disabled} />
      <span>{option.label}</span>
    </div>
  );
}
